import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { authHeader } from "../../auth";
import { OPERATORS, groupBySections, matchesSection, toSections } from "../../utils/printSort";

/**
 * PrintSortManager — « Tri impression » de la liste de départ
 * (07.10.2026, 2e version). Liste ordonnée de SECTIONS définies par des
 * critères (ex. « Genre = W », « Catégorie = WU18 », plusieurs critères =
 * ET). Chaque concurrent va dans la première section qui lui correspond,
 * les autres dans « Others » à la fin ; ordre de départ conservé dans
 * chaque section. AUCUNE donnée des concurrents n'est modifiée : seul
 * config.printSort est enregistré. Même tri pour l'écran et le PDF.
 * Les critères portent sur les concurrents de la liste de départ (prop
 * `startlist`), utilisés aussi pour l'aperçu et les valeurs proposées.
 */

const FIELD_LABELS = { category: "Catégorie", gender: "Genre", association: "Association", club: "Club", nation: "Nation", birthyear: "Année", inscription: "Inscription", bib: "Dossard" };
const NOT_USEFUL = ["order", "lastname", "firstname", "fiscode", "transponder", "addedBy"];
const newRule = () => ({ field: "gender", op: "eq", value: "" });
const genderName = (v) => (/^[WFLD]/i.test(v) ? "Women" : /^[MH]/i.test(v) ? "Men" : v);

export default function PrintSortManager({ open, onClose, eventKey, startlist = [], onSaved }) {
    const [sections, setSections] = useState([]);
    const [pageBreak, setPageBreak] = useState(false);
    const [quickField, setQuickField] = useState("gender");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const key = encodeURIComponent(eventKey);
    const api = (url, options = {}) =>
        fetch(url, { ...options, headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) } });

    useEffect(() => {
        if (!open) return;
        setError("");
        api(`/api/timing/swissski-config/${key}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((c) => {
                setSections(toSections(c?.printSort, c?.categories).map((s) => ({ label: s.label || "", rules: (s.rules || []).map((r) => ({ ...r })) })));
                setPageBreak(!!c?.printSort?.pageBreak);
            })
            .catch((err) => setError(err.message));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, eventKey]);

    const fields = useMemo(() => {
        const set = new Set();
        startlist.forEach((a) => Object.keys(a).forEach((k) => set.add(k)));
        const rest = [...set].filter((f) => !NOT_USEFUL.includes(f) && !["gender", "category"].includes(f)).sort();
        return ["gender", "category", ...rest];
    }, [startlist]);

    const valuesOf = (field) =>
        [...new Set(startlist.map((a) => String(a[field] ?? "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    // Aperçu : nombre de concurrents par section (première section correspondante).
    const preview = useMemo(() => {
        const groups = groupBySections(startlist, { sections }, []);
        const counts = sections.map(() => 0);
        let others = 0;
        startlist.forEach((a) => {
            const i = sections.findIndex((s) => (s.rules || []).some((r) => r.field) && matchesSection(a, s));
            if (i === -1) others++;
            else counts[i]++;
        });
        return { counts, others, groups };
    }, [startlist, sections]);

    const setSection = (i, patch) => setSections((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
    const setRule = (i, ri, patch) =>
        setSections((prev) => prev.map((s, j) => (j === i ? { ...s, rules: s.rules.map((r, k) => (k === ri ? { ...r, ...patch } : r)) } : s)));
    const moveSection = (i, d) =>
        setSections((prev) => {
            const t = i + d;
            if (t < 0 || t >= prev.length) return prev;
            const next = [...prev];
            [next[i], next[t]] = [next[t], next[i]];
            return next;
        });

    // Une section par valeur d'un champ, dans l'ordre d'apparition dans la
    // liste de départ (ajoutées à la suite des sections existantes).
    const createFromField = () => {
        const seen = [];
        startlist.forEach((a) => {
            const v = String(a[quickField] ?? "").trim();
            if (v && !seen.includes(v)) seen.push(v);
        });
        setSections((prev) => [
            ...prev,
            ...seen.map((v) => ({ label: quickField === "gender" ? genderName(v) : v, rules: [{ field: quickField, op: "eq", value: v }] })),
        ]);
    };

    const save = async () => {
        setError("");
        const clean = sections
            .map((s) => ({ label: String(s.label || "").trim(), rules: (s.rules || []).filter((r) => r.field && String(r.value ?? "").trim() !== "") }))
            .filter((s) => s.rules.length);
        setSaving(true);
        try {
            const fresh = await api(`/api/timing/swissski-config/${key}`);
            const base = (fresh.ok ? await fresh.json() : null) || {};
            const printSort = { sections: clean, pageBreak };
            const r = await api(`/api/timing/swissski-config/${key}`, { method: "POST", body: JSON.stringify({ ...base, printSort }) });
            if (!r.ok) throw new Error(`Enregistrement : HTTP ${r.status}`);
            onSaved?.(printSort);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Tri impression — liste de départ</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sections dans l'ordre d'impression. Chaque concurrent va dans la première section dont il remplit tous les
                    critères ; l'ordre de départ est conservé dans chaque section ; les autres sont imprimés à la fin
                    (« Others »). Les données des concurrents ne sont pas modifiées. Sans section : ordre de départ simple.
                </Typography>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {sections.map((s, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <Typography sx={{ fontWeight: "bold", width: 24 }}>{i + 1}.</Typography>
                            <TextField
                                size="small"
                                label="Titre de la section (imprimé)"
                                value={s.label}
                                onChange={(e) => setSection(i, { label: e.target.value })}
                                sx={{ flex: 1, maxWidth: 360 }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
                                {preview.counts[i] || 0} concurrent(s)
                            </Typography>
                            <Box sx={{ ml: "auto", whiteSpace: "nowrap" }}>
                                <IconButton size="small" onClick={() => moveSection(i, -1)} disabled={i === 0}>
                                    <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1}>
                                    <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => setSections((p) => p.filter((_, j) => j !== i))}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>

                        {s.rules.map((r, ri) => (
                            <Box key={ri} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, pl: 4, flexWrap: "wrap" }}>
                                <Typography variant="body2" sx={{ width: 28 }} color="text.secondary">
                                    {ri === 0 ? "si" : "et"}
                                </Typography>
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <Select value={r.field} onChange={(e) => setRule(i, ri, { field: e.target.value, value: "" })}>
                                        {fields.map((f) => (
                                            <MenuItem key={f} value={f}>
                                                {FIELD_LABELS[f] || f}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <Select value={r.op} onChange={(e) => setRule(i, ri, { op: e.target.value })}>
                                        {OPERATORS.map((o) => (
                                            <MenuItem key={o.value} value={o.value}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={valuesOf(r.field)}
                                    value={r.value}
                                    onInputChange={(_, v) => setRule(i, ri, { value: v })}
                                    renderInput={(params) => <TextField {...params} placeholder="valeur" />}
                                    sx={{ minWidth: 200 }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => setSection(i, { rules: s.rules.filter((_, k) => k !== ri) })}
                                    disabled={s.rules.length === 1}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        <Button size="small" sx={{ ml: 4 }} startIcon={<AddIcon />} onClick={() => setSection(i, { rules: [...s.rules, newRule()] })}>
                            Ajouter un critère (ET)
                        </Button>
                    </Paper>
                ))}

                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mt: 1 }}>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setSections((p) => [...p, { label: "", rules: [newRule()] }])}>
                        Ajouter une section
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                        ou créer une section par valeur de
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Champ</InputLabel>
                        <Select label="Champ" value={quickField} onChange={(e) => setQuickField(e.target.value)}>
                            {fields.map((f) => (
                                <MenuItem key={f} value={f}>
                                    {FIELD_LABELS[f] || f}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button onClick={createFromField}>Créer</Button>
                    {sections.length > 0 && (
                        <Button color="error" onClick={() => setSections([])}>
                            Tout effacer
                        </Button>
                    )}
                </Box>

                <FormControlLabel
                    sx={{ mt: 2, display: "block" }}
                    control={<Checkbox checked={pageBreak} onChange={(e) => setPageBreak(e.target.checked)} />}
                    label="Une page par section dans le PDF"
                />

                {sections.length > 0 && (
                    <Alert severity={preview.others ? "warning" : "info"} sx={{ mt: 1 }}>
                        Aperçu : {preview.groups.map((g) => `${g.category} (${g.entries.length})`).join(" → ") || "—"}
                        {preview.others ? ` — ${preview.others} concurrent(s) dans aucune section → « Others » à la fin.` : ""}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Annuler</Button>
                <Button variant="contained" onClick={save} disabled={saving}>
                    {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}